import React, { Suspense, lazy, useEffect } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { TendUI } from '@10d/tend-ui/theme/TendUI';
import { Layout } from '@10d/tend-ui-primitives';
import { Normalize } from '@10d/tend-ui-styling';
import { MuseoSansCyrl } from '@10d/tend-ui-fonts';
import { ISamoletChat } from '@devdept/samolet-chat/dist/types/samoletChat';
import { useSelector } from 'react-redux';
import { NHeaderTestIds } from '@webcommon/types';
import { Toast } from '@10d/tend-ui/primitives';
import { ChatProvider, useChat } from 'context/Chat';
import { GlobalStyle } from 'styled';
import AppRoutes from 'routes/AppRoutes';
import { ScrollYPositionProvider } from 'context/ScrollYPositon';
import { ScrollListPositionProvider } from 'context/ScrollListPositon';
import { WebsocketsProvider } from 'context/Websockets';
import VersionChecker from 'features/VersionChecker';
import { ToggleConsoleService } from 'store/common/toggle/ConsoleService';
import { Dialog } from 'components/Dialog';
import { instance } from 'api/request';
import { selectFeature } from 'store/common/toggle';
import withErrorBoundary from 'hocs/withErrorBoundary';
import { useProfileInit } from 'hooks/profile';
import useAfterRedirectUrlRestore from 'hooks/useAfterRedirectUrlRestore';
import { useInitialRequestsToCache } from 'hooks/useInitialRequestsToCache';
import { useModalStoreController } from 'store/Modal';
import useLoggers from 'hooks/useLoggers';
import { useCSI } from 'hooks/useCSI';
import { useDispatchWaffles } from 'hooks/useWaffle';
import { useRegionsObserver } from 'hooks/useRegionsObserver';
import useInitHistoryStack from 'hooks/useHistoryStack';
import { useInitApp } from 'helpers/initApp';
import Header from 'features/Header';
import useBindProfileDI from './core/profile/di/useBindProfileDI';
import { FullScreenSpinner } from './components/Spinner';

const SamoletChat = lazy(() =>
  import('@devdept/samolet-chat').then(({ SamoletChat }) => ({ default: SamoletChat })),
);

const ModalGallery = lazy(() => import('modals/Common/ModalGallery'));

const SuspenseModalGallery = () => (
  <Suspense fallback={null}>
    <ModalGallery />
  </Suspense>
);

const SuspenseChat: React.FC<React.PropsWithChildren<ISamoletChat>> = props => (
  <Suspense fallback={null}>
    <SamoletChat {...props} />
  </Suspense>
);

Toast.init();
TendUI.init();
Toast.config({
  // 85px - это высота header + 5px отступа
  top: 85,
});

const Root = () => {
  const { isEnabled: isChatHidden } = useSelector(selectFeature('hideChat'));
  const chat = useChat();

  useEffect(() => {
    (window as any).toggle = new ToggleConsoleService();
  }, []);

  return (
    <>
      <GlobalStyle />

      <WebsocketsProvider>
        <VersionChecker>
          <ScrollYPositionProvider>
            <ScrollListPositionProvider>
              {!isChatHidden && <SuspenseChat {...chat} />}
              <AppRoutes />
            </ScrollListPositionProvider>
          </ScrollYPositionProvider>
        </VersionChecker>
      </WebsocketsProvider>

      <SuspenseModalGallery />
    </>
  );
};

const UnderRouterContent: React.FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <ChatProvider>
      <Root />
    </ChatProvider>
  );
};
const UnderRouterWithErrorBoundary = withErrorBoundary(UnderRouterContent);

const WithUi: React.FC<React.PropsWithChildren<unknown>> = () => {
  useCSI();

  return (
    <TendUI client={instance}>
      <Layout.Root>
        <Normalize />
        <MuseoSansCyrl />
        <Dialog.Styles />
        <Toast.Styles />
        {/* для e2e */}
        <div data-testid={NHeaderTestIds.HEADER}>
          <Header />
        </div>

        <Layout.Main>
          <UnderRouterWithErrorBoundary />
        </Layout.Main>

        <ReactQueryDevtools initialIsOpen={false} />
      </Layout.Root>
    </TendUI>
  );
};
const WithControllers: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { isInitializated } = useProfileInit();

  useAfterRedirectUrlRestore();
  useInitialRequestsToCache();
  useModalStoreController();
  useLoggers();
  useDispatchWaffles();
  useRegionsObserver();
  useInitHistoryStack();

  if (!isInitializated) return <FullScreenSpinner />;

  return <WithUi />;
};
export const WithDI: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { isInitializated } = useBindProfileDI();

  useInitApp();

  if (!isInitializated) return <FullScreenSpinner />;

  return <WithControllers />;
};
